/**
 * Murphy Door Configurator - Gate Visibility Manager
 *
 * PURPOSE: Progressive-disclosure DOM visibility, several independent layers:
 * 1. Gate-answer-driven (e.g. "Cabinets? -> Top Only" hides Bottom Cabinet
 *    Doors, shows Top Cabinet Doors) -- re-applied after every tab switch,
 *    since showCategoriesForTab() unconditionally re-shows every category
 *    belonging to the active tab and would otherwise silently undo a gate's
 *    hide the moment the user revisits that tab.
 * 2. Real-selection-driven (e.g. Top Knobs & Pulls stays hidden until Top
 *    Cabinet Doors has an actual applied style, not just the gate's
 *    auto-applied "no doors" fallback) -- a second, independent dependency
 *    layer keyed off SELECTION_VISIBILITY_DEPS, deferred while a cascade
 *    auto-selection is in progress to avoid show/hide/show flicker.
 * 3. Gate-card-depends-on-gate-card -- GATE_CARD_DEFINITIONS.dependsOnGateId.
 *    No gate declares one today (the only edge was Hinge Side -> Swing
 *    Direction, removed 2026-08-27); the loop stays because adding the field
 *    to a gate definition is a one-line data change that should just work.
 * 4. Combination-replaced categories -- GATE_COMBINATION_RESOLUTIONS names a
 *    category whose value is produced by combining several gates' answers,
 *    so the category itself is never shown directly. That array is EMPTY as
 *    of 2026-08-27: its only entry hid "Door Handing" behind the Swing +
 *    Hinge gate pair, and Door Handing now renders as one card with its own
 *    4 variant tiles.
 *
 * REMOVED 2026-08-27 (~600 lines) along with those gates: updateSwingGateAvailability()
 * and its subtree (getGateAnswerBlockReason, getForbiddenReasonText,
 * isVariantForbidden, resolveVariantIdByTitle, gateOptionLabel,
 * applyGateOptionBlockedState, registerBlockedOptionTooltip) plus their two
 * event subscriptions, and applyGateOptionImages()/resolveVariantImageForTitle().
 *
 * That subtree is deliberately NOT worth resurrecting for a future combination
 * gate. It existed only because the resolved category was permanently
 * display:none: a forbidden combination refused its apply against an invisible
 * card and auto-navigation stranded the customer, so this module had to
 * pre-compute -- via a counterfactual re-validation of every applied selection
 * -- which gate ANSWERS would be refused, and grey them itself. A visible
 * category gets that for free from the standard preemptive-forbidden sweep in
 * md-config-ui-updater.js, per variant, with the real rule text. Any future
 * combination gate should keep its resolved category visible rather than
 * re-import the workaround.
 *
 * PHASE: 2 (Event-Driven CCP - Strict Enforcement)
 * MODULE TYPE: UI (ui-) -- Official DOM manipulator per CCP-RULES.md
 *   violation #4 exception, same architectural slot as
 *   md-config-ui-horizontal-slider.js and md-config-ui-tooltip-manager.js.
 *   As a designated md-config-ui-* controller, direct DOM manipulation
 *   (document.querySelector / element.style) is authorized here -- the
 *   same style md-config-ui-tooltip-manager.js already uses.
 * GOLDEN STANDARD REFERENCE: md-config-ui-tooltip-manager.js (pure
 *   event-subscription hygiene, zero direct module-to-module calls).
 *
 * CCP COMPLIANCE:
 * - Reads gate answers via conductor._getGateAnswer() delegation
 * - Reads real applied selections via conductor.getAppliedSelectionsMap()
 *   and resolves product handles via conductor.getVirtualizedProductsForCategory()
 *   -- the same delegation pattern md-config-bl-gate-card-coordinator.js
 *   already uses for its own handle resolution, and the same
 *   subscribe-to-selection:applied-then-read-live-state pattern already
 *   established by md-config-bl-forbidden-validator.js and
 *   md-config-bl-simplified-mode-coordinator.js.
 * - DOM manipulation only, no direct BL module calls
 * - Publishes a completion event only, never calls another module directly
 * - Tracks cascade-in-progress via BUSINESS_CASCADE_AUTO_SELECTION_STARTED/
 *   _COMPLETED/_FAILED (the same externally-observable pattern
 *   md-config-bl-auto-navigation-coordinator.js already uses to defer its
 *   own work around cascades) rather than reaching into the cascade
 *   coordinator's private state.
 *
 * DATA FLOW:
 * business:gate_answer_changed        -> applyAllGateVisibility()
 * ui:categories_shown_for_tab         -> applyAllGateVisibility() (re-assert after sweep)
 * selection:applied                   -> applySelectionDrivenVisibility() (deferred if cascade in progress)
 * business:cascade_auto_selection_completed/_failed -> flush deferred selection-driven check
 */
(function (window) {
  'use strict';

  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};
  const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;

  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // ============================================================================
  // PATTERN 6: BOOTSTRAP RUNTIME PROTECTION
  // ============================================================================
  let bootstrapComplete = false;
  let pendingCalls = [];

  // dependentCategory -> the parent category's applied productId last seen by
  // applySelectionDrivenVisibility(). Drives the content re-render there; see
  // that function for why a hidden->visible transition is not enough.
  const lastParentProductByCategory = new Map();

  // PATTERN 15: CLEANUP ARRAY
  let cleanupHandlers = [];

  // Cascade-in-progress tracking (externally observed, not a private flag
  // read from the cascade coordinator -- see module doc comment).
  let cascadeInProgress = false;
  let pendingSelectionCheck = false;

  // Gates the user has explicitly reopened (via UI_GATE_REOPEN_REQUESTED)
  // but hasn't picked a new answer for yet. applyGateAnsweredState() re-runs
  // on every tab switch (UI_CATEGORIES_SHOWN_FOR_TAB) -- without this guard,
  // switching tabs while a reopened gate is mid-edit would silently re-derive
  // data-answered from the still-stored old answer and snap it closed again
  // before the user got to pick a different one.
  const reopenedGateIds = new Set();

  /**
   * Toggles a gate card between its "asking" state (options row visible)
   * and its "answered" state (collapsed, blue background, matching a real
   * completed category) via data-answered + the .category-title /
   * .category-user-selection classes createGateCardElement() already reuses.
   * Daniel's explicit ask: once answered, the gate should behave like every
   * other card, not stay permanently expanded. Idempotent -- safe to call
   * every time applyAllGateVisibility() runs, not just on an actual change.
   * @param {Object} conductor
   */
  function applyGateAnsweredState(conductor) {
    const gateDefs = CONSTANTS.GATE_CARD_DEFINITIONS;
    if (!conductor || !gateDefs) return;

    gateDefs.forEach((gateDef) => {
      const el = document.querySelector(
        '[data-category-gate="' + CSS.escape(gateDef.id) + '"]'
      );
      if (!el) return;
      const answer = conductor._getGateAnswer
        ? conductor._getGateAnswer(gateDef.id)
        : null;
      const option = answer
        ? gateDef.options.find((o) => o.value === answer)
        : null;
      const summaryEl = el.querySelector('[data-gate-answer-summary]');
      const showAsAnswered = !!option && !reopenedGateIds.has(gateDef.id);
      // WCAG 4.1.2 / axe nested-interactive (2026-09-28): in apo_style an
      // answered gate keeps its option <button>s on screen
      // (section-md-apo-mode.css), so a role="button" card would WRAP those
      // buttons -- the same nested-interactive defect removed from real
      // category headers (CONSTANTS.CATEGORY_HEADER). The options are
      // directly operable there, so the card needs no control role at all;
      // a pointer click on the card still reopens via the event handler.
      // Classic/dual hide the options once answered, so the collapsed card
      // is the only control and keeps its disclosure-button contract.
      const optionsStayVisible =
        conductor._getConfiguratorExperience?.() === 'apo_style';
      if (showAsAnswered && optionsStayVisible) {
        el.setAttribute('data-answered', 'true');
        el.removeAttribute('role');
        el.removeAttribute('tabindex');
        el.removeAttribute('aria-expanded');
        el.removeAttribute('aria-label');
        if (summaryEl) summaryEl.textContent = option.label;
      } else if (showAsAnswered) {
        el.setAttribute('data-answered', 'true');
        el.setAttribute('role', 'button');
        el.setAttribute('tabindex', '0');
        // P6 A11Y: the answered card is a collapsed disclosure trigger --
        // clicking/Enter-Space reopens it (UI_GATE_REOPEN_REQUESTED below),
        // same aria-expanded="false"-on-collapse convention real category
        // headers use (md-config-ui-navigation-manager.js collapseCategory()).
        el.setAttribute('aria-expanded', 'false');
        el.setAttribute(
          'aria-label',
          gateDef.labelHTML + ': ' + option.label + '. Click to change.'
        );
        if (summaryEl) summaryEl.textContent = option.label;
      } else {
        el.setAttribute('data-answered', 'false');
        el.removeAttribute('role');
        el.removeAttribute('tabindex');
        el.removeAttribute('aria-expanded');
        el.removeAttribute('aria-label');
        if (summaryEl) summaryEl.textContent = '';
      }

      // Mark which option button matches the stored answer, regardless of
      // open/closed state, so reopening a previously-answered gate shows
      // the prior pick highlighted instead of a blank options row.
      el.querySelectorAll(
        '.murphy-door-configurator__gate-card-option'
      ).forEach((btn) => {
        const isSelected =
          !!answer && btn.getAttribute('data-answer') === answer;
        btn.classList.toggle(
          'murphy-door-configurator__gate-card-option--selected',
          isSelected
        );
        // P7 A11Y: set in the SAME statement as the class it mirrors. Before
        // this, the chosen answer was conveyed by colour alone -- a screen
        // reader heard four identical buttons ("None", "Top Only", ...) with
        // no indication which one the customer had already picked.
        btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      });
    });
  }

  /**
   * Re-applies every configured gate's CURRENT answer to its dependent
   * categories' DOM visibility. Idempotent -- safe to call on every tab
   * switch, not just on an actual answer change.
   */
  function applyAllGateVisibility() {
    if (!bootstrapComplete) {
      pendingCalls.push({ fn: applyAllGateVisibility, args: [] });
      return;
    }

    const conductor = window.MurphyDoorConfigurator.conductor;
    const gateDeps =
      conductor?._getGateVisibilityDeps?.() ||
      CONSTANTS.CATEGORY_VISIBILITY_DEPS;
    if (!conductor || !gateDeps) return;
    if (!(conductor._areGateCardsEnabled && conductor._areGateCardsEnabled())) {
      return; // no-op when gate cards are disabled for this experience
    }

    applyGateAnsweredState(conductor);

    let updatedCount = 0;

    Object.keys(gateDeps).forEach((gateId) => {
      const gateConfig = gateDeps[gateId];
      const answer = conductor._getGateAnswer
        ? conductor._getGateAnswer(gateId)
        : null;
      // Unanswered gate: hide every dependent category until the user
      // answers -- the progressive-disclosure default, not an error state.
      const visibleCategories =
        answer && gateConfig.answers[answer] ? gateConfig.answers[answer] : [];

      gateConfig.categories.forEach((categoryName) => {
        const el = getCategoryButtonElement(categoryName);
        if (!el) return;
        // Gate visibility is necessary but NOT sufficient -- a category the
        // gate says "visible" must ALSO belong to the currently active tab,
        // or it bleeds into every other tab the instant showCategoriesForTab()
        // fires there too (confirmed live: "Top Cabinet Doors" answered
        // visible under the gate rendered inside the Appearance tab, above
        // Wood & Finish, because this check was missing and
        // UI_CATEGORIES_SHOWN_FOR_TAB fires on every tab, not just Features).
        const belongsToActiveTab = belongsToActiveScope(el, conductor);
        const shouldBeVisible =
          belongsToActiveTab && visibleCategories.indexOf(categoryName) !== -1;
        el.style.display = shouldBeVisible ? '' : 'none';
        updatedCount++;
      });
    });

    // Gate-card-depends-on-gate-card (e.g. "Hinge Side" only makes sense
    // once "Swing Direction" has an answer -- Left Hand Inswing and Left
    // Hand Outswing are different real variants, so picking a side before a
    // direction is meaningless). Re-asserted every call for the same reason
    // the category loop above is: a tab-sweep or apo_style reveal-all would
    // otherwise unconditionally re-show every gate card, dependent one
    // included.
    const gateCardDefs = CONSTANTS.GATE_CARD_DEFINITIONS || [];
    gateCardDefs.forEach((gateDef) => {
      if (!gateDef.dependsOnGateId) return;
      const el = document.querySelector(
        '[data-category-gate="' + CSS.escape(gateDef.id) + '"]'
      );
      if (!el) return;
      const parentAnswer = conductor._getGateAnswer
        ? conductor._getGateAnswer(gateDef.dependsOnGateId)
        : null;
      const belongsToActiveTab = belongsToActiveScope(el, conductor);
      el.style.display = parentAnswer && belongsToActiveTab ? '' : 'none';
      updatedCount++;
    });

    // Categories fully replaced by a GATE_COMBINATION_RESOLUTIONS entry
    // (e.g. "Door Handing" replaced by the Swing + Hinge gates) stay hidden
    // entirely once gate cards are enabled -- there is no gate "answer" that
    // reveals them, they are never shown directly, only auto-applied by
    // md-config-bl-gate-card-coordinator.js. Confirmed with Daniel
    // 2026-08-11: fully replace, not a visible manual-override fallback.
    const combinationResolutions = CONSTANTS.GATE_COMBINATION_RESOLUTIONS || [];
    combinationResolutions.forEach((resolution) => {
      const el = getCategoryButtonElement(resolution.category);
      if (!el) return;
      el.style.display = 'none';
      updatedCount++;
    });

    if (updatedCount > 0 && CoreEventBus) {
      CoreEventBus.publish(
        CONSTANTS.EVENTS.UI_GATE_DEPENDENT_CATEGORIES_VISIBILITY_UPDATED,
        {
          categoryCount: updatedCount,
          timestamp: Date.now(),
          source: 'GateVisibilityManager',
        }
      );
    }
  }

  /**
   * Re-assert BOTH progressive-disclosure layers.
   *
   * The two layers are one verdict, not two features: the gate answer decides
   * whether Cabinet Doors may show, and the applied cabinet style decides
   * whether Knobs & Pulls may show. Any sweep that resets category `display`
   * invalidates BOTH, so every such trigger must restore both -- in that order,
   * since the selection layer's parent is a category the gate layer owns.
   *
   * 🚨 This exists because the layers used to be re-asserted ASYMMETRICALLY.
   * SELECTION_APPLIED and the cascade-completion flush ran only the
   * selection-driven half, so any event that disturbed visibility WITHOUT
   * firing UI_CATEGORIES_SHOWN_FOR_TAB left the gate half stale, with no
   * second chance. Measured live on production 2026-08-31 (?config= restore,
   * "Cabinets?" unanswered): the gate hid Top/Bottom Cabinet Doors at t=6126,
   * md-config-ui-navigation-manager.js's BUSINESS_SIMPLIFIED_MODE_COMPLETE
   * sweep un-hid every category button at t=6375, and nothing re-ran the gate
   * layer -- so the customer got cabinet and knobs cards for a question they
   * never answered. Route every trigger through here rather than picking a
   * layer per call site; picking is how the two drifted apart.
   */
  function reassertAllVisibility() {
    applyAllGateVisibility();
    applySelectionDrivenVisibility();
  }

  /**
   * The currently active tab's id. Confirmed live: `aria-selected` is set
   * exclusively by md-config-ui-navigation-manager.js's activateTab() (a
   * direct-call path) and is NEVER touched by the real tab-click path
   * (event-handler -> conductor -> uiUpdater.updateTabDisplay(), which
   * toggles the .active CLASS only, not the ARIA attribute) -- two parallel,
   * only-partially-overlapping tab-activation systems in this codebase.
   * Reading aria-selected here silently returns whatever tab was last set
   * via activateTab(), stale the instant a real click fires. The .active
   * class is the one signal both paths agree on.
   * @returns {string|null}
   */
  function getActiveTabId() {
    const activeTabButton = document.querySelector('.murphy-tab-button.active');
    return activeTabButton ? activeTabButton.getAttribute('data-tab') : null;
  }

  /**
   * The tab-membership requirement above only makes sense when tabs exist.
   * apo_style renders no tab bar at all (md-config-ui-navigation-manager.js's
   * reveal-all pass) -- there is no ".murphy-tab-button.active" to match,
   * so getActiveTabId() always returns null there, which would make every
   * gate-dependent category permanently invisible (belongsToActiveTab never
   * true). In apo_style every category is simultaneously "on screen", so
   * tab-membership is vacuously true -- skip the check entirely instead of
   * evaluating it against a tab system that isn't running.
   * @param {HTMLElement} el
   * @param {Object} conductor
   * @returns {boolean}
   */
  function belongsToActiveScope(el, conductor) {
    if (conductor?._getConfiguratorExperience?.() === 'apo_style') return true;
    return el.getAttribute('data-tab') === getActiveTabId();
  }

  /**
   * Scoped to the real category button specifically -- an unscoped
   * [data-category="..."] selector also matches
   * .murphy-door-configurator__category-layer (an image-compositing
   * element that shares the same data-category value), and querySelector
   * returns whichever is first in DOM order, which is NOT reliably the
   * button. Confirmed live: an unscoped selector here silently toggled the
   * wrong element and never hid the intended card.
   * @param {string} categoryName
   * @returns {HTMLElement|null}
   */
  function getCategoryButtonElement(categoryName) {
    return document.querySelector(
      '.murphy-door-configurator__category-button[data-category="' +
        CSS.escape(categoryName) +
        '"]'
    );
  }

  /**
   * Re-applies SELECTION_VISIBILITY_DEPS to its dependent categories' DOM
   * visibility, based on the REAL applied selection in each dependency's
   * parent category. Deferred (queued, not executed) while a cascade
   * auto-selection is in progress -- multiple SELECTION_APPLIED events can
   * fire in rapid succession during a cascade, and toggling DOM on every
   * one risks a visible show->hide->show flicker before the cascade settles.
   */
  function applySelectionDrivenVisibility() {
    if (!bootstrapComplete) {
      pendingCalls.push({ fn: applySelectionDrivenVisibility, args: [] });
      return;
    }
    if (cascadeInProgress) {
      pendingSelectionCheck = true;
      return;
    }

    const conductor = window.MurphyDoorConfigurator.conductor;
    // Via the conductor so an admin selection_dependency_rule and the
    // constant can never disagree between this and the three other sites
    // that read the same map.
    const deps =
      conductor?._getSelectionVisibilityDeps?.() ??
      CONSTANTS.SELECTION_VISIBILITY_DEPS;
    if (!conductor || !deps) return;
    if (!(conductor._areGateCardsEnabled && conductor._areGateCardsEnabled())) {
      return; // no-op outside apo_style, same scoping as applyAllGateVisibility()
    }

    let updatedCount = 0;

    Object.keys(deps).forEach((dependentCategory) => {
      const dep = deps[dependentCategory];
      const el = getCategoryButtonElement(dependentCategory);
      if (!el) return;

      const appliedMap = conductor.getAppliedSelectionsMap
        ? conductor.getAppliedSelectionsMap()
        : null;
      const parentSelection = appliedMap
        ? appliedMap.get(dep.dependsOnCategory)
        : null;

      let shouldBeVisible = false;
      if (parentSelection && parentSelection.productId) {
        const products = conductor.getVirtualizedProductsForCategory
          ? conductor.getVirtualizedProductsForCategory(
              dep.dependsOnCategory
            ) || []
          : [];
        const product = products.find(
          (p) => p && p.id === parentSelection.productId
        );
        // Visible once a REAL style is chosen -- i.e. a real applied
        // selection exists AND it is not the "no doors" fallback handle.
        shouldBeVisible = !!(
          product && product.handle !== dep.hiddenWhenProductHandle
        );
      }

      // Same tab-membership requirement as applyAllGateVisibility() -- see
      // that function's comment for why this is necessary, not optional.
      const belongsToActiveTab = belongsToActiveScope(el, conductor);
      el.style.display = shouldBeVisible && belongsToActiveTab ? '' : 'none';

      // Re-render whenever the PARENT selection changes.
      //
      // This function only ever toggled `display`, which was fine while cards
      // were populated lazily: the category's tile grid was built by
      // showCategoryContent() at the moment auto-navigation opened it, i.e.
      // AFTER the parent cabinet style had been applied. apo_style now builds
      // every card up front (_forceAllCategoriesOpenForApoStyle), so this
      // grid was generated at t=0 with no cabinet door applied and froze in
      // that state -- Daniel 2026-08-17: "chosing a cabinet option doesnt
      // load the knobs and pulls, right now the option sits as just the none
      // option and it cannot be selected." Revealing a stale grid is worse
      // than revealing none, because it looks populated.
      //
      // Keyed on the parent's productId rather than on a hidden->visible
      // transition: knobs depend on WHICH cabinet door is applied, not merely
      // on one being applied, so swapping Shaker for Slab has to refresh them
      // too even though the card never hides in between. Storing the key also
      // stops this from re-rendering on every unrelated selection, which
      // would clobber whatever the customer was doing inside that card.
      //
      // The map must record "content HAS been rendered for this parent",
      // never merely "this parent has been seen". Writing the key
      // unconditionally poisons the memo: if the render throws, or if the
      // card isn't visible yet because getVirtualizedProductsForCategory()
      // transiently returned [], the key is stored with nothing rendered and
      // the category can NEVER retry for that parent -- it just reveals the
      // stale t=0 grid, silently, since isDev() is false in production.
      // So: only store on a successful render, and clear the key whenever the
      // card is not visible so the next reveal always re-renders.
      const parentKey = parentSelection?.productId || null;
      const refreshNeeded =
        shouldBeVisible &&
        belongsToActiveTab &&
        lastParentProductByCategory.get(dependentCategory) !== parentKey;

      if (!shouldBeVisible || !belongsToActiveTab) {
        lastParentProductByCategory.delete(dependentCategory);
      }

      if (refreshNeeded) {
        // KNOWN CCP DEVIATION, taken deliberately. CCP-RULES.md rule 2 has no
        // ui->ui exception, so this direct uiUpdater call is a coupling the
        // letter of the rules would route through an event instead. It is
        // kept because the event form would be strictly worse here: publishing
        // is fire-and-forget, so this module could not know whether the render
        // actually succeeded, and the memo directly above depends on exactly
        // that signal to avoid marking a failed card as rendered. Trading a
        // real correctness guarantee for a documented-ambiguous style rule is
        // the wrong way round. The sibling ui module
        // (md-config-ui-navigation-manager.js) already calls uiUpdater
        // directly in several places, so this is consistent with the layer's
        // actual architecture rather than a new pattern. Revisit if a
        // request/response event contract is ever added.
        const uiUpdater = window.MurphyDoorConfigurator.uiUpdater;
        if (typeof uiUpdater?.showCategoryContent === 'function') {
          try {
            uiUpdater.showCategoryContent(dependentCategory);
            lastParentProductByCategory.set(dependentCategory, parentKey);
          } catch (error) {
            isDev() &&
              console.error(
                '[GateVisibilityManager] Failed to refresh content for "' +
                  dependentCategory +
                  '" on reveal:',
                error
              );
            if (CoreEventBus) {
              CoreEventBus.publish(
                CONSTANTS.EVENTS.UI_CATEGORY_EXPANSION_FAILED,
                {
                  categoryId: dependentCategory,
                  error: error && error.message ? error.message : String(error),
                  timestamp: Date.now(),
                  source: 'GateVisibilityManager.selectionDrivenRefresh',
                }
              );
            }
          }
        }
      }
      updatedCount++;
    });

    if (updatedCount > 0 && CoreEventBus) {
      CoreEventBus.publish(
        CONSTANTS.EVENTS.UI_GATE_DEPENDENT_CATEGORIES_VISIBILITY_UPDATED,
        {
          categoryCount: updatedCount,
          timestamp: Date.now(),
          source: 'GateVisibilityManager.selectionDriven',
        }
      );
    }
  }

  // ============================================================================
  // BOOTSTRAP SUBSCRIPTION (MODULE LOAD TIME - RACE PROTECTION, Golden Pattern #9)
  // ============================================================================
  if (CoreEventBus) {
    const unsubBootstrap = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      () => {
        bootstrapComplete = true;
        if (pendingCalls.length > 0) {
          pendingCalls.forEach((call) => {
            try {
              call.fn.apply(null, call.args);
            } catch (error) {
              isDev() &&
                console.error(
                  '[GateVisibilityManager] Error processing queued call:',
                  error
                );
            }
          });
          pendingCalls = [];
        }
      }
    );
    cleanupHandlers.push(unsubBootstrap);

    const unsubAnswerChanged = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.BUSINESS_GATE_ANSWER_CHANGED,
      (eventData) => {
        // A real new answer was picked (including re-picking the same gate
        // after a reopen) -- clear the reopened-guard so this gate is
        // allowed to show its answered/collapsed state again.
        if (eventData?.gateId) {
          reopenedGateIds.delete(eventData.gateId);
        }
        applyAllGateVisibility();
      }
    );
    cleanupHandlers.push(unsubAnswerChanged);

    // Clicking an answered (collapsed, blue) gate card reopens it so the
    // user can pick a different answer -- Daniel's explicit ask: "the
    // ability to go back into the cabinets? choice and edit it." Purely a
    // DOM toggle (data-answered back to false); the stored answer in
    // sessionStorage/_gateAnswerCache is untouched until the user actually
    // clicks a new option, at which point BUSINESS_GATE_ANSWER_CHANGE_REQUESTED
    // fires normally through the existing conductor._setGateAnswer() path.
    const unsubReopen = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.UI_GATE_REOPEN_REQUESTED,
      (eventData) => {
        const gateId = eventData?.gateId;
        if (!gateId) return;
        reopenedGateIds.add(gateId);

        // Reopening must also mark this gate data-open='true' (and close
        // any other open gate) -- otherwise its now-data-answered='false'
        // options stay hidden per the collapsed-until-current CSS rule in
        // section-md-configurator.css, and the user clicks "reopen" into an
        // empty card. Mirrors updateCategoryDisplay()'s close-others/open-
        // target pattern for real categories.
        document.querySelectorAll('[data-category-gate]').forEach((el) => {
          const isTarget = el.getAttribute('data-category-gate') === gateId;
          el.setAttribute('data-open', isTarget ? 'true' : 'false');
          // Must pair with the aria-current='step' set below -- without
          // this, reopening gate B while gate A was already the current
          // step (Daniel's explicit "go back and edit" ask) left BOTH
          // claiming to be the active step to AT users, since this sweep
          // only ever set aria-current, never cleared it on the others.
          // Mirrors the pairing already correct in
          // md-config-ui-navigation-manager.js's UI_GATE_OPEN_REQUEST
          // handler and md-config-ui-updater.js's updateCategoryDisplay().
          // Final-verification gauntlet pass, 2026-08-13 -- the fixture in
          // this file's own test only ever built one gate card, so no
          // existing test could catch a leftover aria-current on a SECOND
          // gate.
          if (!isTarget) el.removeAttribute('aria-current');
        });

        // Cross-type close: reopening a gate while a real category happens
        // to be open (its own CATEGORY_OPEN class + data-open) must close
        // that category too, or both panels render expanded at once.
        // Gauntlet finding 2026-08-13, mirrors the same fix in
        // md-config-ui-navigation-manager.js's UI_GATE_OPEN_REQUEST handler.
        // MUST close via Animations.slideClose (not a raw classList.remove)
        // so any pending slideOpen timer for this category is cancelled too
        // -- a second gauntlet pass caught this block reintroducing the
        // exact stale-timer race the animationKey guard elsewhere in this
        // PR was built to close.
        {
          const uiUpdaterAnimations =
            window.MurphyDoorConfigurator.uiUpdaterHelpers?.Animations;

          // Exactly ONE element may claim aria-current="step". The gate sweep
          // above clears only other GATES, and the collapse loop below never
          // touched aria-current at all -- so reopening a gate while a real
          // category was the current step left BOTH claiming it. Kept outside
          // the apo_style guard below because it is correct in every mode.
          document
            .querySelectorAll(
              '.murphy-door-configurator__category-button[data-category][aria-current]'
            )
            .forEach((el) => el.removeAttribute('aria-current'));

          // apo_style cards never collapse (Ryan review 2026-08-17), so the
          // cross-type close is skipped there -- reopening a gate must not
          // slam every open real category shut. This mirrors the guard on the
          // identical sweep in md-config-ui-navigation-manager.js's
          // UI_GATE_OPEN_REQUEST handler; both twins need it, not just one.
          const _apoConductor = window.MurphyDoorConfigurator.conductor;
          const _isApoStyle =
            _apoConductor?._getConfiguratorExperience?.() === 'apo_style';

          (_isApoStyle
            ? []
            : document.querySelectorAll(
                '.murphy-door-configurator__category-button--open[data-category]'
              )
          ).forEach((el) => {
            el.classList.remove(
              'murphy-door-configurator__category-button--open'
            );
            el.setAttribute('data-open', 'false');
            el.setAttribute('aria-expanded', 'false');
            const openCategoryId = el.getAttribute('data-category');
            const thumbs = el.querySelector(
              '.murphy-door-configurator__thumbnails'
            );
            if (thumbs && uiUpdaterAnimations) {
              uiUpdaterAnimations.slideClose(
                thumbs,
                'category-slide:' + openCategoryId
              );
            } else if (thumbs) {
              thumbs.classList.remove(
                'murphy-door-configurator__thumbnails--open'
              );
            }
          });
        }

        const conductor = window.MurphyDoorConfigurator.conductor;
        applyGateAnsweredState(conductor);

        // Reopened gate is now the current, options-visible step. It's a
        // role-less <div> at this point (applyGateAnsweredState only adds
        // role="button" once ANSWERED, since only then is the card itself a
        // click target), so aria-expanded would be inert to AT here --
        // aria-current="step" conveys "this is the active step" without
        // needing a supporting interactive role. Must run AFTER
        // applyGateAnsweredState() to win over its unanswered-state cleanup.
        // Gauntlet findings 2026-08-13: accessibility + mobile-responsive-ux
        // first flagged the missing AT signal (aria-expanded attempt);
        // semantic-html then caught that attempt setting aria-expanded on a
        // role-less div, corrected to aria-current here.
        const reopenedGate = document.querySelector(
          '[data-category-gate="' + CSS.escape(gateId) + '"]'
        );
        if (reopenedGate) reopenedGate.setAttribute('aria-current', 'step');
      }
    );
    cleanupHandlers.push(unsubReopen);

    // A reopened-for-editing gate got force-closed by a REAL category
    // opening (not by the user picking a new answer) -- e.g. reopen Swing,
    // then open Door Depth. md-config-ui-updater.js's updateCategoryDisplay()
    // already set data-open='false' on the gate; this only needs to clear
    // the reopened-guard and re-collapse it to its answered/blue state,
    // since the stored answer never actually changed. Without this, the
    // gate is left permanently showing blank/unanswered (reopenedGateIds
    // only ever cleared on a genuine new BUSINESS_GATE_ANSWER_CHANGED).
    // Live-reproduced 2026-08-13 (Daniel): Swing turned visually white and
    // auto-navigation landed back on the Cabinets gate after this exact
    // sequence.
    const unsubForceClosed = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.UI_GATE_FORCE_CLOSED,
      (eventData) => {
        if (eventData?.gateId) {
          reopenedGateIds.delete(eventData.gateId);
        }
        applyGateAnsweredState(window.MurphyDoorConfigurator.conductor);
      }
    );
    cleanupHandlers.push(unsubForceClosed);

    // Re-assert after every tab switch -- showCategoriesForTab() unconditionally
    // re-shows every category belonging to the active tab, which would
    // otherwise undo a gate's hide (see module doc comment above). Also
    // re-asserts the selection-driven layer (Phase 2): with no selection
    // made yet, Knobs & Pulls must default to hidden the first time their
    // tab is shown, not just react after a selection happens.
    const unsubTabShown = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.UI_CATEGORIES_SHOWN_FOR_TAB,
      reassertAllVisibility
    );
    cleanupHandlers.push(unsubTabShown);

    // Simplified mode / ?config= URL restoration finishing hands control back
    // to the full configurator, and md-config-ui-navigation-manager.js clears
    // inline display on EVERY category button on the way out. That sweep is
    // deliberately gate-blind (it must not re-derive the gate answer), so it
    // announces UI_CATEGORIES_SHOWN_FOR_TAB and the handler above restores the
    // verdict.
    //
    // Subscribing here as well is defence in depth, not redundancy: it makes
    // the guarantee "the gate verdict survives simplified-mode completion"
    // hold even if a future sweep on this event forgets to announce. The
    // handlers are idempotent -- they recompute from the gate answer and the
    // applied-selections map and write the same display either way -- so the
    // double call costs two passes over two categories and cannot oscillate.
    const unsubSimplifiedComplete = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.BUSINESS_SIMPLIFIED_MODE_COMPLETE,
      reassertAllVisibility
    );
    cleanupHandlers.push(unsubSimplifiedComplete);

    // Phase 2: real-selection-driven Knobs & Pulls reveal.
    const unsubSelectionApplied = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.SELECTION_APPLIED,
      () => {
        applySelectionDrivenVisibility();
      }
    );
    cleanupHandlers.push(unsubSelectionApplied);

    const unsubCascadeStarted = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_STARTED,
      () => {
        cascadeInProgress = true;
      }
    );
    cleanupHandlers.push(unsubCascadeStarted);

    const flushPendingSelectionCheck = () => {
      cascadeInProgress = false;
      pendingSelectionCheck = false;
      // Re-assert BOTH layers, unconditionally, once the cascade settles.
      //
      // Previously this ran the selection layer only, and only when a
      // SELECTION_APPLIED had been deferred mid-cascade. That missed the case
      // the ?config= restore actually hits: a cascade completing can trigger
      // downstream DOM sweeps (BUSINESS_SIMPLIFIED_MODE_COMPLETE is published
      // straight off cascade completion by
      // md-config-bl-simplified-mode-coordinator.js) which reset category
      // display without any selection having been deferred -- so the flag was
      // false, nothing re-ran, and the gate verdict stayed clobbered.
      //
      // Dropping the flag guard costs one extra idempotent pass over two
      // categories per cascade and removes a stale-flag failure mode; the
      // deferral itself still does its job of collapsing mid-cascade churn.
      reassertAllVisibility();
    };
    const unsubCascadeCompleted = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_COMPLETED,
      flushPendingSelectionCheck
    );
    cleanupHandlers.push(unsubCascadeCompleted);

    const unsubCascadeFailed = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_FAILED,
      flushPendingSelectionCheck
    );
    cleanupHandlers.push(unsubCascadeFailed);
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================
  window.MurphyDoorConfigurator.gateVisibilityManager = {
    cleanup: function () {
      cleanupHandlers.forEach((unsub) => {
        try {
          if (typeof unsub === 'function') unsub();
        } catch (error) {
          isDev() &&
            console.error('[GateVisibilityManager] Cleanup error:', error);
        }
      });
      cleanupHandlers = [];
    },
    getState: function () {
      return Object.freeze({
        bootstrapComplete: bootstrapComplete,
        pendingCallCount: pendingCalls.length,
        subscriptionCount: cleanupHandlers.length,
        cascadeInProgress: cascadeInProgress,
        pendingSelectionCheck: pendingSelectionCheck,
      });
    },
  };

  isDev() && console.log('[GateVisibilityManager] Module loaded');
})(window);
